import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BarChart, Breadcrumbs, ChartFrame, HexMark, LineChart, SectionLabel, SimulatedTag, StatusDot, StatusPill } from '@hive/ui';
import { api, errorMessage, type NetworkOverview, type NetworkScreenDetail } from '../api/client';
import { useLive, useNow } from '../api/hooks';
import { ScreenThumb } from '../components/ScreenThumb';
import { Shell } from '../components/Shell';
import { useAuth } from '../auth/AuthContext';
import { AD_RIGHTS, SCTE35 } from '../components/StreamForm';
import { ago, hhmm, hhmmss, until } from '../format';

/** Ring gauge for the QoE composite (MC-Screen). */
function QoeGauge({ score, threshold }: { score?: number; threshold: number }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const frac = score != null ? score / 100 : 0;
  const cls = score == null ? 'mc-gauge__arc--none' : score < threshold ? 'mc-gauge__arc--bad' : 'mc-gauge__arc--ok';
  return (
    <svg viewBox="0 0 84 84" className="mc-gauge" role="img" aria-label={score != null ? `QoE ${score}` : 'QoE not available'}>
      <circle cx="42" cy="42" r={r} className="mc-gauge__track" />
      <circle cx="42" cy="42" r={r} className={`mc-gauge__arc ${cls}`} strokeDasharray={`${c * frac} ${c}`} transform="rotate(-90 42 42)" />
      <text x="42" y="44" textAnchor="middle" className="mc-gauge__value">
        {score ?? '—'}
      </text>
      <text x="42" y="58" textAnchor="middle" className="mc-gauge__label">
        QoE
      </text>
    </svg>
  );
}

function behind(seconds: number): string {
  return seconds >= 90 ? `${Math.round(seconds / 60)} min` : `${seconds} s`;
}

/** What went wrong with the stream in the last hour, in the player's own reports. */
function StreamHealthCard({ health, now }: { health: NonNullable<NetworkScreenDetail['streamHealth']>; now: number }) {
  const failing = health.errorsLastHour > 0;
  return (
    <section aria-label="Stream health" className="hv-card mc-block">
      <SectionLabel dense as="span">
        Stream health
      </SectionLabel>
      {failing ? (
        <p className="mc-cause">
          <StatusDot status="degraded" /> {health.cause ? health.cause.charAt(0).toUpperCase() + health.cause.slice(1) : 'Playback errors'}
        </p>
      ) : (
        <p className="mc-small hv-muted">No stream errors in the last hour.</p>
      )}
      {failing && (
        <div className="mc-grid2">
          <div className="hv-kv">
            <span className="hv-kv__key">Errors in the last hour</span>
            <span className="hv-kv__value">{health.errorsLastHour >= 500 ? '500+' : health.errorsLastHour}</span>
          </div>
          <div className="hv-kv">
            <span className="hv-kv__key">Latest</span>
            <span className="hv-kv__value">{ago(health.errors[0]?.at, now)} ago</span>
          </div>
        </div>
      )}
      {(failing || health.rejoins.length > 0) && (
        <ol className="mc-feed mc-feed--cues" aria-label="Stream events, newest first">
          {[
            ...health.errors.slice(0, 5).map((e) => ({
              at: e.at,
              text: (
                <>
                  {e.httpStatus ? `HTTP ${e.httpStatus}` : (e.code ?? 'Error').replace(/^ERROR_CODE_/, '').replace(/_/g, ' ').toLowerCase()}
                  {e.host ? <span className="hv-muted"> · {e.host}</span> : null}
                  {e.onBackup ? <span className="hv-muted"> · backup address</span> : null}
                  {e.streamName ? <span className="hv-muted"> · {e.streamName}</span> : null}
                </>
              ),
            })),
            ...health.rejoins.map((r) => ({
              at: r.at,
              text: <>Jumped back to live{r.behindSeconds ? <span className="hv-muted"> · was {behind(r.behindSeconds)} behind</span> : null}</>,
            })),
          ]
            .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
            .slice(0, 8)
            .map((x, i) => (
              <li key={`${x.at}-${i}`}>
                <span className="mc-feed__time">{hhmmss(x.at)}</span>
                <span>{x.text}</span>
              </li>
            ))}
        </ol>
      )}
    </section>
  );
}

/** SCTE-35 markers the player saw, next to what the stream it is playing allows. */
function AdMarkersCard({ markers, now }: { markers: NonNullable<NetworkScreenDetail['adMarkers']>; now: number }) {
  const { cues } = markers;
  const rights = markers.adRights ? AD_RIGHTS[markers.adRights] : undefined;
  return (
    <section aria-label="Ad markers" className="hv-card mc-block">
      <SectionLabel dense as="span">
        Ad markers
      </SectionLabel>
      <div className="mc-grid2">
        <div className="hv-kv">
          <span className="hv-kv__key">Stream</span>
          <span className="hv-kv__value">{markers.streamName ?? 'Nothing playing'}</span>
        </div>
        <div className="hv-kv">
          <span className="hv-kv__key">Ad rights</span>
          <span className="hv-kv__value" title={rights?.hint}>
            {rights?.label ?? '—'}
          </span>
        </div>
        <div className="hv-kv">
          <span className="hv-kv__key">Markers declared</span>
          <span className="hv-kv__value">{markers.carriage ? SCTE35[markers.carriage] : '—'}</span>
        </div>
        <div className="hv-kv">
          <span className="hv-kv__key">Seen in the last hour</span>
          <span className="hv-kv__value">
            {cues.length === 0 ? 'None' : `${cues.length}${cues.length >= 20 ? '+' : ''}, last ${ago(cues[0]!.at, now)} ago`}
          </span>
        </div>
      </div>
      {cues.length > 0 && (
        <ol className="mc-feed mc-feed--cues" aria-label="Markers, newest first">
          {cues.slice(0, 8).map((c, i) => (
            <li key={`${c.at}-${i}`}>
              <span className="mc-feed__time">{hhmmss(c.at)}</span>
              <span>
                Break{c.durationSeconds ? ` · ${c.durationSeconds} s` : ''}
                {c.id ? <span className="hv-muted"> · event {c.id}</span> : null}
                {c.streamName && c.streamName !== markers.streamName ? <span className="hv-muted"> · on {c.streamName}</span> : null}
              </span>
            </li>
          ))}
        </ol>
      )}
      <p className="mc-small hv-muted">Reported by the player. Ads are not inserted at markers yet.</p>
    </section>
  );
}

export function ScreenPage() {
  const { screenId = '' } = useParams();
  const now = useNow(5000);
  const { data: network, live } = useLive<NetworkOverview>(async () => (await api.GET('/v1/network')).data, [], 20_000);
  const { data, error, refresh } = useLive<NetworkScreenDetail>(
    async () => (await api.GET('/v1/network/screens/{screenId}', { params: { path: { screenId } } })).data,
    [screenId],
  );
  const { user } = useAuth();
  const canOpenStream = Boolean(user?.permissions.includes('streams.manage'));
  const [explain, setExplain] = useState(false);
  const [restart, setRestart] = useState<{ busy: boolean; message?: string; error?: boolean }>({ busy: false });

  async function doRestart() {
    setRestart({ busy: true });
    try {
      const { data: result, error } = await api.POST('/v1/network/screens/{screenId}/restart', { params: { path: { screenId } } });
      setRestart(result ? { busy: false, message: `${result.message}. The player will reload in a few seconds.` } : { busy: false, error: true, message: errorMessage(error) });
      void refresh();
    } catch {
      setRestart({ busy: false, error: true, message: 'Could not reach the server' });
    }
  }

  if (error && !data) {
    return (
      <Shell network={network} live={live}>
        <div className="mc-page hv-muted">This screen does not exist in your tenant.</div>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell network={network} live={live}>
        <div className="mc-page hv-muted">Loading screen…</div>
      </Shell>
    );
  }

  const s = data.screen;
  const sim = s.source === 'simulated';
  const q = data.qoe;
  const window = { from: now - 3600_000, to: now };
  const statusLabel =
    s.status === 'offline' ? `Offline · last heartbeat ${ago(s.lastHeartbeatAt, now)} ago` : s.status === 'unpaired' ? 'No player paired' : `${s.reportedState === 'playing' ? 'Playing' : s.reportedState ?? 'Online'} · ${q.score != null && q.score < q.threshold ? 'degraded' : 'healthy'}`;

  return (
    <Shell network={network} live={live}>
      <div className="mc-page">
        <header className="mc-pagehead">
          <div className="mc-pagehead__left">
            <Breadcrumbs
              items={[
                { label: 'Network', href: '/network' },
                { label: data.venue.name, href: `/venue/${data.venue.id}` },
                {
                  label: (
                    <>
                      <HexMark size={20} tone={s.status === 'offline' ? 'red' : 'teal'} />
                      {s.name}
                      {s.code ? ` · ${s.code}` : ''}
                      {sim && <SimulatedTag title="Simulated screen" />}
                    </>
                  ),
                },
              ]}
              renderLink={(href, label) => <Link to={href}>{label}</Link>}
            />
            <StatusPill status={s.status === 'offline' ? 'offline' : s.status === 'unpaired' ? 'idle' : q.score != null && q.score < q.threshold ? 'degraded' : 'healthy'}>{statusLabel}</StatusPill>
          </div>
          <div className="mc-pagehead__actions">
            <button type="button" className="hv-btn hv-btn--secondary hv-btn--sm" disabled title={data.actions.switchEdge.reason}>
              Switch CDN edge
            </button>
            <button
              type="button"
              className="hv-btn hv-btn--primary hv-btn--sm"
              disabled={!data.actions.restart.available || restart.busy}
              title={data.actions.restart.reason ?? 'Sends a restart command to the real player over its live connection'}
              onClick={doRestart}
            >
              {restart.busy ? 'Restarting…' : 'Restart player'}
            </button>
            <button type="button" className="hv-btn hv-btn--secondary hv-btn--sm" disabled title={data.actions.takeOver.reason}>
              Take over screen
            </button>
          </div>
        </header>
        {restart.message && (
          <p className={restart.error ? 'mc-note mc-note--bad' : 'mc-note mc-note--ok'} role="status">
            {restart.message}
          </p>
        )}

        <div className="mc-screen-grid">
          <div className="mc-col">
            <section aria-label="Live preview" className="hv-card mc-preview">
              <ScreenThumb screen={s} large />
              <div className="mc-row-between mc-preview__bar">
                <span className="mc-small">
                  <StatusDot status={s.status === 'online' ? 'live' : 'offline'} />{' '}
                  {s.preview ? `Live preview · captured ${ago(s.preview.capturedAt, now)} ago · low-res proxy` : 'No preview received from this player'}
                </span>
                {data.nowPlaying?.score && (
                  <span className="mc-small mc-strong">
                    {data.nowPlaying.score}
                    {data.nowPlaying.clock ? ` · ${data.nowPlaying.clock}` : ''} <SimulatedTag title="Score is simulated" />
                  </span>
                )}
              </div>
              <div className="mc-grid2 mc-preview__info">
                <div className="hv-kv">
                  <span className="hv-kv__key">Now playing</span>
                  <span className="hv-kv__value">{data.nowPlaying?.title ?? (s.status === 'online' ? 'Ambient screen' : '—')}</span>
                  <span className="hv-muted mc-tiny">
                    {[data.nowPlaying?.entitlementId && `Entitlement ${data.nowPlaying.entitlementId}`, data.nowPlaying?.feedCode && `feed ${data.nowPlaying.feedCode}`, data.nowPlaying?.rendition].filter(Boolean).join(' · ')}
                  </span>
                  {data.nowPlaying?.streamId && (
                    <span className="mc-tiny">
                      {canOpenStream ? (
                        <Link to={`/streams/${data.nowPlaying.streamId}`}>Stream: {data.nowPlaying.streamName}</Link>
                      ) : (
                        <span className="hv-muted">Stream: {data.nowPlaying.streamName}</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="hv-kv">
                  <span className="hv-kv__key">Next on this screen</span>
                  <span className="hv-kv__value">{data.next?.label ?? 'Nothing scheduled'}</span>
                  {data.next?.detail && <span className="hv-muted mc-tiny">{data.next.detail}</span>}
                </div>
              </div>
            </section>
            <div className="mc-charts">
              <ChartFrame title="Delivered bitrate · 60 min" meta={s.bitrateKbps.value != null ? `${(s.bitrateKbps.value / 1000).toFixed(1)} Mbps` : undefined} source={data.bitrateSeries.source === 'simulated' ? 'simulated' : undefined}>
                <LineChart
                  points={data.bitrateSeries.points}
                  from={window.from}
                  to={window.to}
                  min={0}
                  annotations={data.bitrateSeries.annotations}
                  ariaLabel="Delivered bitrate over the last 60 minutes, with ABR steps marked"
                  formatY={(v) => `${v.toFixed(v < 10 ? 1 : 0)}`}
                />
              </ChartFrame>
              <ChartFrame title="Rebuffer events · per 5 min" meta={s.rebufferRatio.value != null ? `${s.rebufferRatio.value}% ratio` : undefined} source={data.rebufferBuckets.source === 'simulated' ? 'simulated' : undefined}>
                <BarChart buckets={data.rebufferBuckets.points} ariaLabel="Rebuffer events per five minutes over the last hour" />
              </ChartFrame>
            </div>
          </div>

          <div className="mc-col">
            <section aria-label="Quality of experience" className="hv-card mc-qoe">
              <div className="mc-qoe__head">
                <QoeGauge score={q.score} threshold={q.threshold} />
                <div className="mc-qoe__text">
                  <span className="mc-strong">
                    {q.score != null ? `${q.label}${q.stableForMinutes != null ? ` · stable for ${q.stableForMinutes} min` : ''}` : s.status === 'online' ? 'Not playing video' : 'No measurements'}
                    {q.source === 'simulated' && <SimulatedTag />}
                  </span>
                  <span className="hv-muted mc-small">
                    Composite of rebuffer ratio, bitrate against ladder ceiling, live latency and dropped frames. Threshold for a degraded alert is {q.threshold}.
                  </span>
                  <button type="button" className="hv-btn hv-btn--link hv-btn--sm mc-explain" aria-expanded={explain} onClick={() => setExplain((e) => !e)}>
                    {explain ? 'Hide the weighting' : 'How the score is calculated'}
                  </button>
                </div>
              </div>
              {explain && (
                <div className="mc-explainer">
                  <p className="mc-small">{q.explanation}</p>
                  {q.components.length > 0 && (
                    <table className="mc-table">
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>Now</th>
                          <th>Points off</th>
                          <th>Max</th>
                        </tr>
                      </thead>
                      <tbody>
                        {q.components.map((c) => (
                          <tr key={c.name}>
                            <td>{c.name}</td>
                            <td>{c.detail}</td>
                            <td>{c.penalty}</td>
                            <td>{c.weight}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              <div className="mc-grid2">
                {data.details.map((d) => (
                  <div key={d.label} className="hv-kv">
                    <span className="hv-kv__key">{d.label}</span>
                    <span className="hv-kv__value">
                      {d.value}
                      {d.source === 'simulated' && <SimulatedTag />}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {data.streamHealth && <StreamHealthCard health={data.streamHealth} now={now} />}

            {data.playback && (
              <section aria-label="Player resilience" className="hv-card mc-block">
                <SectionLabel dense as="span">
                  If Hive is unreachable
                </SectionLabel>
                <div className="mc-grid2">
                  <div className="hv-kv">
                    <span className="hv-kv__key">Keeps playing until</span>
                    <span className="hv-kv__value">
                      {data.playback.leaseExpiresAt ? `${hhmm(data.playback.leaseExpiresAt)} (in ${until(data.playback.leaseExpiresAt, now)})` : 'Not reported yet'}
                    </span>
                  </div>
                  <div className="hv-kv">
                    <span className="hv-kv__key">Stream address</span>
                    <span className={`hv-kv__value${data.playback.usingBackup ? ' mc-warn' : ''}`}>{data.playback.usingBackup ? 'On the backup, after errors on the main one' : 'Main'}</span>
                  </div>
                  <div className="hv-kv">
                    <span className="hv-kv__key">Last cut off</span>
                    <span className="hv-kv__value">
                      {data.playback.lastOutage
                        ? `${ago(data.playback.lastOutage.startedAt, now)} ago, for ${Math.max(1, Math.round((Date.parse(data.playback.lastOutage.endedAt) - Date.parse(data.playback.lastOutage.startedAt)) / 60_000))} min (${data.playback.lastOutage.reason})`
                        : 'Not since it started'}
                    </span>
                  </div>
                </div>
              </section>
            )}

            {data.adMarkers && <AdMarkersCard markers={data.adMarkers} now={now} />}

            <section aria-label="Rating and rights on this screen" className="hv-card mc-block">
              <SectionLabel dense as="span">
                Screen rating and rights
              </SectionLabel>
              <div className="mc-grid2">
                {data.rights.map((d) => (
                  <div key={d.label} className="hv-kv">
                    <span className="hv-kv__key">{d.label}</span>
                    <span className="hv-kv__value">
                      {d.value}
                      {d.source === 'simulated' && <SimulatedTag />}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section aria-label="Recent events" className="hv-card mc-block">
              <SectionLabel dense as="span">
                Recent events
              </SectionLabel>
              <ol className="mc-feed">
                {data.events.map((e, i) => (
                  <li key={i}>
                    <span className="mc-feed__time">{hhmm(e.at)}</span>
                    <StatusDot status={e.kind === 'disconnected' ? 'offline' : e.kind === 'restart' ? 'degraded' : 'healthy'} />
                    <span>
                      {e.message}
                      {e.source === 'simulated' && <SimulatedTag />}
                    </span>
                  </li>
                ))}
                {data.events.length === 0 && <li className="hv-muted">No events yet</li>}
              </ol>
            </section>
          </div>
        </div>
      </div>
    </Shell>
  );
}
